import React, { useState, useRef } from 'react'
import { Report } from "../../utils/loaders";
import Web3Modal from 'web3modal';
import WalletConnect from "@walletconnect/web3-provider";
import { ethers } from 'ethers';
import { getXdcModal } from 'xdcpay-web3modal'
import { useDispatch, useSelector } from "react-redux";
import { setWeb3 } from '../../reducers/web3Reducer';
import {
    getActiveChainId, getActiveChain, setActiveChainId, getChain, getEnabledChains, isSupportedChain,
    getWalletConnectRpcMap, getContractAddress, switchWalletToChain, toChainId,
} from '../../config/chains';
import { connectSolanaWallet } from '../../lib/solanaWallet';
import { pickSolanaWallet, pickEvmWallet } from './solanaWalletModal';
import { loginUserViaWallet, attachWallet } from '../../reducers/authReducer';
import { signWalletLoginChallenge, runWalletAuthOnce } from '../../utils/walletAuth';
import { useNavigate } from 'react-router-dom';
import EEGTokenABI from "../../contracts/EEGToken.json";
import SubscribeABI from "../../contracts/Subscribe.json";




export const web3Modal = new Web3Modal({
    cacheProvider: true,
    // disableInjectedProvider: true,
    providerOptions: {
        walletconnect: {
            package: WalletConnect, // required
            options: {
                infuraId: process.env.REACT_APP_INFURA_ID || "27e484dcd9e3efcfd25a83a78777cdf1",
                // Built from the chain registry (config/chains.js) so every
                // enabled network — XDC and Avalanche — is available.
                rpc: getWalletConnectRpcMap(),
            }
        },
        'custom-xdc': getXdcModal,
    }
});

// BUG-043 fix: the web3modal library renders its wallet-chooser card itself
// (it isn't JSX in this codebase), and it ships with no title and no close
// button — the only way out was clicking outside the card, which isn't
// discoverable. We add a slim empty strip at the top of the card (the wallet
// grid underneath is NOT changed) and put one plain "×" in its top-right
// corner, so it belongs to the whole modal and doesn't sit on any wallet
// option. Clicking it triggers a click on the library's own backdrop
// (.web3modal-modal-hitbox), which is the library's real close path — it
// hides the card AND tells the pending connect() that the user closed it.
// (web3Modal.toggleModal() is NOT used here: with cacheProvider on, it
// reconnects to the cached wallet instead of closing.)
if (typeof document !== 'undefined') {
    const CLOSE_BTN_ID = 'ee-wallet-modal-close-btn';
    const TOP_STRIP_PX = 40; // height of the empty strip that holds the "×"

    const ensureCloseButton = () => {
        const card = document.querySelector('.web3modal-modal-card');
        if (!card) return;

        const existing = document.getElementById(CLOSE_BTN_ID);
        if (existing && existing.parentNode === card) return;
        if (existing) existing.remove(); // leftover from an older version

        // Empty strip above the grid — only adds space at the top, the
        // grid's columns/cells are left exactly as the library draws them.
        card.style.paddingTop = `${TOP_STRIP_PX}px`;

        const closeBtn = document.createElement('button');
        closeBtn.id = CLOSE_BTN_ID;
        closeBtn.type = 'button';
        closeBtn.innerHTML = '&times;';
        closeBtn.setAttribute('aria-label', 'Close');
        closeBtn.style.cssText = `
            position: absolute;
            top: 6px;
            right: 12px;
            width: 28px;
            height: 28px;
            padding: 0;
            border: none;
            background: transparent;
            color: #4a5568;
            font-size: 24px;
            line-height: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            z-index: 10;
        `;
        closeBtn.onmouseenter = () => { closeBtn.style.color = '#000'; };
        closeBtn.onmouseleave = () => { closeBtn.style.color = '#4a5568'; };
        closeBtn.onclick = (e) => {
            e.stopPropagation();
            const hitbox = document.querySelector('.web3modal-modal-hitbox');
            if (hitbox) hitbox.click();
        };

        card.appendChild(closeBtn);
    };

    // The library creates its modal as soon as `new Web3Modal(...)` runs
    // (above), so try right away, again once the page has fully loaded, and
    // again whenever the page adds elements (it does nothing if the button
    // is already in place).
    ensureCloseButton();
    window.addEventListener('load', ensureCloseButton);

    const walletModalObserver = new MutationObserver(ensureCloseButton);
    walletModalObserver.observe(document.body, { childList: true, subtree: true });
}


// Network switching lives in config/chains.js (switchWalletToChain) — generic
// for every chain in the registry rather than hardcoded to Apothem.

// Builds the per-chain contract instances from that chain's addresses. A
// contract that isn't deployed/configured on the chain comes back as null
// (instead of ethers throwing on an empty address) so callers can show a
// clear "not available on this network" message.
const buildChainContracts = (provider, chainId) => {
    const make = (key, abi) => {
        const address = getContractAddress(key, chainId);
        if (!address || !ethers.utils.isAddress(address)) return null;
        return new ethers.Contract(address, abi, provider);
    };
    return {
        eegtokenInstance: make('eeg', EEGTokenABI),
        subscribeInstance: make('subscribe', SubscribeABI),
    };
};

// Forces the wallet to show its account-selection prompt, even if this site
// was already connected before. wallet_requestPermissions is part of the
// EIP-2255 permissions API and — unlike wallet_revokePermissions (used in
// Logout.js) — has been supported by MetaMask since 2021, so it works on
// wallet versions where revokePermissions silently no-ops.
//
// Why this matters: without it, an injected wallet's eth_requestAccounts
// silently returns whatever account was previously authorized for this
// site, even after switching the active account in the wallet extension —
// requestPermissions is the one call in the standard permissions API that
// is specified to always re-prompt, which is exactly the "let the user
// actually choose/confirm the account" behavior a login flow needs.
//
// Skipped for WalletConnect sessions — account selection there already
// happens as part of approving the QR/deep-link session, and WalletConnect
// providers don't reliably implement this injected-wallet-specific method.
const requestFreshAccountSelection = async (instance) => {
    if (!instance || typeof instance.request !== 'function' || instance.isWalletConnect) return;
    try {
        await instance.request({
            method: 'wallet_requestPermissions',
            params: [{ eth_accounts: {} }],
        });
    } catch (e) {
        // User dismissed the picker, or this wallet doesn't support the
        // method — fall through and let listAccounts() below use whatever
        // is already authorized rather than blocking the connect entirely.
    }
};

// targetChainId: the network the user picked in the selector. When given and
// the wallet is on a different (or unsupported) chain, the wallet is asked to
// switch to it. When omitted (auto-reconnect, chainChanged), the app simply
// FOLLOWS whatever supported chain the wallet is already on.
// Solana path of onWalletConnect: same result shape, no ethers provider.
// A button click (forceAccountPicker) may open the wallet's popup; a background
// restore (App.js) connects silently and does nothing when the site is not approved.
const onSolanaWalletConnect = async ({ forceAccountPicker, chain }) => {
    try {
        // A button click opens the wallet picker (same idea as the EVM modal); a
        // background restore stays silent and never shows it.
        if (forceAccountPicker) {
            const picked = await pickSolanaWallet();
            if (!picked) return;
        }
        const conn = await connectSolanaWallet({ silent: !forceAccountPicker, chainId: chain.chainId });
        if (!conn) return;
        setActiveChainId(chain.chainId);
        return {
            provider: null,
            address: conn.address,
            signer: conn.signer,
            chainId: chain.chainId,
            chain,
            family: 'solana',
            walletName: conn.wallet,
            web3Modal: null,
            eegtokenInstance: null,
            subscribeInstance: null,
        };
    } catch (e) {
        if (e && e.code === 'NO_SOLANA_WALLET') {
            Report.warning('No Solana Wallet', e.message, 'OK');
        } else {
        }
    }
};

export const onWalletConnect = async ({ forceAccountPicker = false, targetChainId = null } = {}) => {

    // The selected network decides the wallet family (the active chain follows the
    // wallet and is remembered, so a page-load restore picks the right path too).
    const wanted = getChain(targetChainId) || getActiveChain();
    if (wanted && wanted.family === 'solana') {
        return onSolanaWalletConnect({ forceAccountPicker, chain: wanted });
    }

    try {
        let instance;
        if (forceAccountPicker) {
            // Connect button: always show our wallet picker, then connect to the chosen wallet.
            // Reuse the real wallet logos the web3modal library already ships with.
            const logos = {};
            try {
                (web3Modal.providerController.getUserOptions() || []).forEach((o) => { if (o && o.id && o.logo) logos[o.id] = o.logo; });
            } catch (e) {}
            const walletId = await pickEvmWallet(logos);
            if (!walletId) return;
            try { web3Modal.clearCachedProvider(); } catch (e) {}
            instance = await web3Modal.connectTo(walletId);
            if (!web3Modal.cachedProvider) {
                try { web3Modal.providerController.setCachedProvider(walletId); } catch (e) {}
            }
            await requestFreshAccountSelection(instance);
        } else {
            // Background restore (page load / chain change): never show a picker or a
            // wallet popup. Only reconnect a wallet the user already connected earlier.
            if (!web3Modal.cachedProvider) return;
            instance = await web3Modal.connect();
            try {
                const approved = await instance.request({ method: 'eth_accounts' });
                if (!approved || approved.length === 0) return;
            } catch (e) { return; }
        }

        // eth_requestAccounts, not provider.listAccounts() (eth_accounts) —
        // right after wallet_requestPermissions grants/changes which
        // accounts are exposed, eth_accounts can momentarily still return
        // the previous list (a known MetaMask timing quirk). eth_requestAccounts
        // is the spec-guaranteed authoritative call for "which account(s)
        // does this site currently have access to, with the active one
        // first" — this is what actually reflects the account just picked
        // in the permissions popup.
        let rawAccounts = [];
        try {
            rawAccounts = await instance.request({ method: forceAccountPicker ? 'eth_requestAccounts' : 'eth_accounts' });
        } catch (reqErr) {
        }

        if (!rawAccounts || rawAccounts.length === 0) {
            Report.warning(
                'No Account Connected',
                `No wallet account available. Please unlock your wallet and try again.`,
                'OK',
            );
            return;
        }
        const activeAddress = rawAccounts[0];

        let provider = new ethers.providers.Web3Provider(instance);
        let { chainId } = await provider.getNetwork();

        const wantedChainId = toChainId(targetChainId);
        const walletOnSupported = isSupportedChain(chainId);

        if ((wantedChainId && chainId !== wantedChainId) || !walletOnSupported) {
            // Either the user explicitly picked a network, or the wallet is
            // on one we don't support — ask the wallet to move to the wanted
            // chain (falling back to the currently selected one).
            const switchTo = wantedChainId || getActiveChainId();
            const switched = await switchWalletToChain(instance, switchTo);
            if (switched) {
                provider = new ethers.providers.Web3Provider(instance);
                ({ chainId } = await provider.getNetwork());
            }
            if (!isSupportedChain(chainId)) {
                const names = getEnabledChains().map((c) => c.name).join(' or ');
                Report.warning(
                    'Unsupported Network',
                    `Please switch your wallet to ${names}.`,
                    'OK',
                );
                return;
            }
        }

        // The wallet is the source of truth for the active chain.
        setActiveChainId(chainId);

        //SEND XDC
        // Explicitly pass the address from eth_requestAccounts rather than
        // calling provider.getSigner() with no arguments — the no-arg form
        // internally re-derives the address via listAccounts()/eth_accounts,
        // which is exactly the call with the stale-cache quirk this fix is
        // avoiding.
        const signer = provider.getSigner(activeAddress);
        const address = activeAddress;


        const { eegtokenInstance, subscribeInstance } = buildChainContracts(provider, chainId);

        const web3data = {
            provider,
            address,
            signer,
            chainId,
            chain: getChain(chainId),
            web3Modal,
            eegtokenInstance,
            subscribeInstance
        }
        return web3data;



    } catch (e) {
    }

}

const ConnectButton = ({ children }) => {
    const navigate = useNavigate()

    const [accountAddress, setaccountAddress] = useState()
    const { isAuthenticated, userprofile } = useSelector(state => state.auth);
    const { connected } = useSelector(state => state.web3);
    const [redirect, setredirect] = useState(false);
    const redirectRef = useRef(false);



    const dispatch = useDispatch()

    // NOTE: auto-reconnect-on-mount intentionally lives only in App.js now.
    // This component is rendered in several places (Header, HeaderDark,
    // LoginV2) — having its own copy of this effect meant every one of
    // those firing its own /api/user/walletlogin call at the same moment
    // App.js's did, racing each other.

    const onConnect = async (_redirect = true) => {
        try {
            const web3data = await onWalletConnect({ forceAccountPicker: true, targetChainId: getActiveChainId() });
            if (web3data) {
                setaccountAddress(web3data.address);
                dispatch(setWeb3(web3data));

                // This is the header's global "Connect Wallet" button — visible
                // on every page, including a property page that's ALSO showing
                // its own "wallet doesn't match" warning (Detail.js). Without
                // this check, clicking it would still pop MetaMask's signature
                // prompt and attempt to attach whatever account the picker just
                // returned, even though it's already known (from the profile
                // already loaded into state.auth.userprofile) to be the wrong
                // one for this account. Signing here would accomplish nothing —
                // the backend (POST /api/user/wallet) refuses to overwrite an
                // already-registered wallet with a different one — but a
                // pointless-yet-real MetaMask prompt at this point is exactly
                // the confusing "did it just switch my wallet?" moment being
                // fixed, so stop before ever asking for a signature.
                if (
                    isAuthenticated &&
                    userprofile && userprofile.walletAddress &&
                    userprofile.walletAddress.toLowerCase() !== web3data.address.toLowerCase()
                ) {
                    Report.warning(
                        'Different Wallet Connected',
                        `Your account is registered with a different wallet:\n${userprofile.walletAddress}\n\nSwitch to that wallet in MetaMask to continue.`,
                        'OK',
                    );
                    return;
                }

                // See runWalletAuthOnce in utils/walletAuth.js: connecting a
                // wallet the site has never seen before fires MetaMask's
                // accountsChanged event, which App.js's listener reacts to by
                // independently running this same nonce -> sign -> submit
                // sequence at the same time as this click handler. Without
                // this lock, both flows fetch/overwrite the same single-use
                // nonce and race to submit it — the loser always fails
                // signature verification, and the user gets asked to sign
                // twice. Whichever flow loses the lock just backs off; the
                // winner's outcome (login or attach) is the one that counts.
                const { skipped } = await runWalletAuthOnce(async () => {
                    let signature;
                    try {
                        signature = await signWalletLoginChallenge(web3data.signer, web3data.address);
                    } catch (sigErr) {
                        Report.warning(
                            'Signature Required',
                            'You need to sign the message in your wallet to log in.',
                            'OK',
                        );
                        return;
                    }

                    // Already signed in (email login) — attach this wallet to
                    // THAT account instead of running walletlogin, which would
                    // log the browser into a completely different account (the
                    // one this wallet already owns, or a brand-new one), silently
                    // swapping the session out from under an email user who just
                    // wanted to connect their wallet.
                    if (isAuthenticated) {
                        const result = await dispatch(attachWallet(web3data.address, signature));
                        if (result.success) {
                            Report.success(
                                'Wallet Connected',
                                'Your wallet has been connected to your account.',
                                'OK',
                            );
                        } else {
                            Report.failure(
                                'Could Not Connect Wallet',
                                (result.error && result.error.error) || 'Failed to connect wallet.',
                                'OK',
                            );
                        }
                        return;
                    }

                    const loginData = {
                        walletAddress: web3data.address,
                        signature,
                    };

                    const result = await dispatch(loginUserViaWallet(loginData));

                    if (result.success && _redirect) {
                        navigate("/user/dashboard");
                    } else if (result.needsRegistration) {
                        // Handle registration flow
                    } else {
                    }
                });

                if (skipped) {
                    // A concurrent flow (most likely App.js's accountsChanged
                    // listener, triggered by this same connection) is already
                    // handling sign-in for this wallet — nothing more to do
                    // here.
                }
            }
        } catch (e) {
        }
    };



    return <React.Fragment>
 <button type="button" onClick={() => onConnect()} className="btn btn-primary btn-sm"><i className="fa fa-wallet"></i> Connect Wallet</button>
    </React.Fragment>

}

export default ConnectButton;